const LEGACY_STATIC_LEMMA_LENGTH = 27
const STATIC_LEMMA_WITH_LINKS_LENGTH = 28
const STATIC_LEMMA_LENGTH = 29

function hasOwn(object, field) {
  return Object.prototype.hasOwnProperty.call(object, field)
}

function normalizeStaticOccurrenceAssociation(occurrence) {
  if (!occurrence || typeof occurrence !== 'object' || Array.isArray(occurrence)) {
    throw new Error('Static lemma occurrence data is malformed')
  }

  const hasCanonical = hasOwn(occurrence, 'lemmaSenseId')
  const hasLegacy = hasOwn(occurrence, 'senseId')
  if (!hasCanonical && !hasLegacy) {
    throw new Error('Static lemma occurrence is missing its Sense association field')
  }
  if (hasCanonical && hasLegacy && occurrence.lemmaSenseId !== occurrence.senseId) {
    throw new Error('Static lemma occurrence has conflicting Sense association fields')
  }

  if (!hasCanonical) {
    occurrence.lemmaSenseId = occurrence.senseId
  }
  // Temporary compatibility alias for existing static consumers.
  if (!hasLegacy) {
    occurrence.senseId = occurrence.lemmaSenseId
  }
  return occurrence
}

function normalizeStaticReferenceAssociations(reference) {
  if (!reference || typeof reference !== 'object' || Array.isArray(reference)) {
    throw new Error('Static lemma reference data is malformed')
  }

  if (!Array.isArray(reference.lemmaSense)) {
    throw new Error('Static lemma reference Sense associations must be an array')
  }
  for (const association of reference.lemmaSense) {
    if (!association || typeof association !== 'object' || typeof association.id !== 'string') {
      throw new Error('Static lemma reference has a malformed Sense association')
    }
  }
  return reference
}

function normalizeStaticLemmaRelations(lemma) {
  if (!lemma || typeof lemma !== 'object' || Array.isArray(lemma)) {
    throw new Error('Static lemma data is missing or malformed after conversion')
  }

  if (!Array.isArray(lemma.occurrences) || !Array.isArray(lemma.references)) {
    throw new Error('Static lemma relationship collections are malformed')
  }

  for (const wrapper of lemma.occurrences) {
    if (!wrapper || typeof wrapper !== 'object' || Array.isArray(wrapper)) {
      throw new Error('Static lemma occurrence wrapper is malformed')
    }
    const occurrences = [wrapper.occurrence, wrapper.containerOccurrence].filter(Boolean)
    if (occurrences.length !== 1) {
      throw new Error('Static lemma occurrence wrapper must contain exactly one occurrence')
    }
    const occurrence = normalizeStaticOccurrenceAssociation(occurrences[0])
    if (typeof occurrence.id !== 'string' || !occurrence.id) {
      throw new Error('Static lemma occurrence is missing its ID')
    }

    const idField = wrapper.occurrence ? 'occurrenceId' : 'containerOccurrenceId'
    const emptyIdField = wrapper.occurrence ? 'containerOccurrenceId' : 'occurrenceId'
    if (hasOwn(wrapper, idField) && wrapper[idField] !== occurrence.id) {
      throw new Error(`Static lemma occurrence wrapper has a conflicting ${idField}`)
    }
    if (hasOwn(wrapper, emptyIdField) && wrapper[emptyIdField] !== null) {
      throw new Error(`Static lemma occurrence wrapper has a conflicting ${emptyIdField}`)
    }
    wrapper[idField] = occurrence.id
    wrapper[emptyIdField] = null
  }

  lemma.references.forEach(normalizeStaticReferenceAssociations)
  return lemma
}

function indexLemmaSensesByAssociation(senses) {
  if (!Array.isArray(senses)) {
    return senses
  }

  const indexed = {}
  for (const sense of senses) {
    const associationId = sense && sense.lemmaSense && sense.lemmaSense.id
    if (typeof associationId !== 'string' || !associationId) {
      throw new Error('Lemma Sense is missing its lemma-sense association ID')
    }
    if (hasOwn(indexed, associationId)) {
      throw new Error(`Duplicate lemma-sense association ID ${associationId}`)
    }
    indexed[associationId] = sense
  }
  return indexed
}

function currentLemmaSenseAssociationIds(lemma) {
  const senses = lemma && lemma.senses
  if (!senses || (typeof senses !== 'object' && !Array.isArray(senses))) {
    return new Set()
  }

  return new Set(Object.values(senses).reduce((ids, sense) => {
    const association = sense && sense.lemmaSense
    if (
      association &&
      typeof association.id === 'string' &&
      association.id &&
      (!association.lemmaId || association.lemmaId === lemma.id)
    ) {
      ids.push(association.id)
    }
    return ids
  }, []))
}

function getUnassignedLemmaItems(lemma) {
  const associationIds = currentLemmaSenseAssociationIds(lemma)
  const candidates = new Map()

  function addCandidate(item, type, assigned) {
    if (!item || typeof item.id !== 'string' || !item.id) return

    const key = `${type}:${item.id}`
    const candidate = candidates.get(key)
    if (candidate) {
      candidate.assigned = candidate.assigned || assigned
      return
    }
    candidates.set(key, { item, type, assigned })
  }

  for (const wrapper of (lemma && lemma.occurrences) || []) {
    if (wrapper && wrapper.occurrence) {
      addCandidate(
        wrapper.occurrence,
        'occurrence',
        associationIds.has(wrapper.occurrence.lemmaSenseId)
      )
    }
    if (wrapper && wrapper.containerOccurrence) {
      addCandidate(
        wrapper.containerOccurrence,
        'containeroccurrence',
        associationIds.has(wrapper.containerOccurrence.lemmaSenseId)
      )
    }
  }

  for (const reference of (lemma && lemma.references) || []) {
    const associations = Array.isArray(reference && reference.lemmaSense)
      ? reference.lemmaSense
      : []
    addCandidate(
      reference,
      'reference',
      associations.some(association => associationIds.has(association && association.id))
    )
  }

  const result = {
    occurrences: [],
    containerOccurrences: [],
    references: [],
    all: [],
  }
  for (const candidate of candidates.values()) {
    if (candidate.assigned) continue

    result.all.push(candidate.item)
    if (candidate.type === 'occurrence') {
      result.occurrences.push(candidate.item)
    } else if (candidate.type === 'containeroccurrence') {
      result.containerOccurrences.push(candidate.item)
    } else {
      result.references.push(candidate.item)
    }
  }
  return result
}

function normalizeStaticLemmaPayload(payload) {
  if (!Array.isArray(payload)) {
    throw new Error('Static lemma data must be a positional array')
  }

  if (payload.length === LEGACY_STATIC_LEMMA_LENGTH) {
    return [
      ...payload.slice(0, -1),
      null,
      [],
      payload[payload.length - 1],
    ]
  }

  if (payload.length === STATIC_LEMMA_WITH_LINKS_LENGTH) {
    return [
      ...payload.slice(0, -2),
      null,
      ...payload.slice(-2),
    ]
  }

  if (payload.length !== STATIC_LEMMA_LENGTH) {
    throw new Error(
      `Static lemma data has ${payload.length} fields; expected ${STATIC_LEMMA_LENGTH}`
    )
  }

  return payload
}

function normalizeLemma(lemma) {
  if (!lemma || typeof lemma !== 'object' || Array.isArray(lemma)) {
    throw new Error('Lemma data is missing or malformed')
  }
  if (lemma.id === null || lemma.id === undefined || !lemma.lemmaForm) {
    throw new Error('Lemma data is missing its required identity or form')
  }

  const arrayFields = [
    'etymologies',
    'occurrences',
    'partofspeeches',
    'scriptas',
    'references',
    'linkedLemmata',
  ]
  const objectFields = [
    'occurrencesSurfaceFormGrouped',
    'occurrencesDictIndex',
    'containerOccurrencesDictIndex',
    'referencesDictIndex',
  ]

  for (const field of arrayFields) {
    if (lemma[field] === null || lemma[field] === undefined) {
      lemma[field] = []
    }
    if (!Array.isArray(lemma[field])) {
      throw new Error(`Lemma field ${field} must be an array`)
    }
  }
  for (const field of objectFields) {
    if (lemma[field] === null || lemma[field] === undefined) {
      lemma[field] = {}
    }
    if (typeof lemma[field] !== 'object' || Array.isArray(lemma[field])) {
      throw new Error(`Lemma field ${field} must be an object`)
    }
  }
  for (const field of ['protospellings', 'senses']) {
    if (lemma[field] === null || lemma[field] === undefined) {
      lemma[field] = []
    }
    if (!Array.isArray(lemma[field]) && typeof lemma[field] !== 'object') {
      throw new Error(`Lemma field ${field} must be an array or object`)
    }
  }

  return lemma
}

export {
  getUnassignedLemmaItems,
  indexLemmaSensesByAssociation,
  normalizeLemma,
  normalizeStaticLemmaRelations,
  normalizeStaticLemmaPayload,
}
